import React, { FC, useRef } from 'react'

import { DraftBlockType, Editor, EditorState, RichUtils } from 'draft-js'
import 'draft-js/dist/Draft.css'

import { TEXT_EDITOR_STYLE_MAP } from '@/constants/textEditor'

function getBlockStyle(block: any) {
  switch (block.getType()) {
    case 'blockquote':
      return 'text-editor-blockquote'
    default:
      return null
  }
}

const StyleButton: FC<any> = ({ onToggle, style, active, label, variation }) => {
  let className = `cursor-pointer px-2 py-1 text-sm text-gray-600`

  if (active) {
    className = 'cursor-pointer bg-gray-200 font-semibold px-3 py-1 text-sm text-black'
  }

  return (
    <span
      className={className + ` ${variation}`}
      onMouseDown={onToggle}
    >
      {label}
    </span>
  )
}

const BlockStyleControls: FC<any> = (props) => {
  const { editorState, onToggle } = props
  const selection = editorState.getSelection()
  const blockType = editorState
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey())
    .getType()
  return (
    <>
      <StyleButton
        key='UL'
        active={blockType === 'unordered-list-item'}
        label='UL'
        onToggle={() => onToggle('unordered-list-item')}
        style='unordered-list-item'
      />
      <StyleButton
        key='OL'
        active={blockType === 'ordered-list-item'}
        label='OL'
        onToggle={() => onToggle('ordered-list-item')}
        style='ordered-list-item'
      />
      <StyleButton
        key='Quote'
        active={blockType === 'blockquote'}
        label='blockquote'
        onToggle={() => onToggle('blockquote')}
        style='blockquote'
      />
    </>
  )
}

const InlineStyleControls: FC<any> = ({ onToggle, editorState }) => {
  const currentStyle = editorState.getCurrentInlineStyle()

  return (
    <>
      <StyleButton
        key='B'
        active={currentStyle.has('BOLD')}
        label='B'
        onToggle={() => onToggle('BOLD')}
        style='BOLD'
        variation='font-extrabold'
      />
      <StyleButton
        key='I'
        active={currentStyle.has('ITALIC')}
        label='I'
        onToggle={() => onToggle('ITALIC')}
        style='ITALIC'
        variation='italic'
      />
      <StyleButton
        key='U'
        active={currentStyle.has('UNDERLINE')}
        label='U'
        onToggle={() => onToggle('UNDERLINE')}
        style='UNDERLINE'
        variation='underline'
      />
      <StyleButton
        key='STRIKETHROUGH'
        active={currentStyle.has('STRIKETHROUGH')}
        label='S'
        onToggle={() => onToggle('STRIKETHROUGH')}
        style='STRIKETHROUGH'
        variation='line-through'
      />
    </>
  )
}

interface CustomTextEditorFieldProps {
  onChange: (editorName: string, editorState: EditorState) => void
  editorName: string
  editorState: EditorState
  placeholder?: string
}

export const CustomTextEditorField: FC<CustomTextEditorFieldProps> = ({
  onChange,
  editorName,
  editorState,
  placeholder = 'Enter text',
}) => {
  const editor = useRef<any>(null)

  const handleChange = (editorState: EditorState) => {
    onChange(editorName, editorState)
  }

  const focus = () => editor.current?.focus()

  const handleKeyCommand = (command: any): any => {
    const newState = RichUtils.handleKeyCommand(editorState, command)

    if (newState) {
      handleChange(newState)
      return true
    }

    return false
  }

  const toggleBlockType = (blockType: DraftBlockType) => {
    handleChange(RichUtils.toggleBlockType(editorState, blockType))
  }

  const toggleInlineStyle = (inlineStyle: string) => {
    handleChange(RichUtils.toggleInlineStyle(editorState, inlineStyle))
  }

  const contentState = editorState.getCurrentContent()
  let className = 'text-[14px] p-5'

  if (!contentState.hasText()) {
    if (contentState.getBlockMap().first().getType() !== 'unstyled') {
      className += ' RichEditor-hidePlaceholder'
    }
  }

  return (
    <div className='border'>
      <div className='flex items-center gap-1 border-b bg-gray-50'>
        <InlineStyleControls
          editorState={editorState}
          onToggle={toggleInlineStyle}
        />
        <span className='mx-1 text-gray-200'>|</span>
        <BlockStyleControls
          editorState={editorState}
          onToggle={toggleBlockType}
        />
      </div>
      <div
        className={className}
        onClick={focus}
      >
        <Editor
          blockStyleFn={getBlockStyle}
          customStyleMap={TEXT_EDITOR_STYLE_MAP}
          editorState={editorState}
          handleKeyCommand={handleKeyCommand}
          onChange={handleChange}
          placeholder={placeholder}
          ref={editor}
          spellCheck={true}
        />
      </div>
    </div>
  )
}

export default CustomTextEditorField
